import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminSidebar from './AdminSidebar';

import { API_BASE_URL } from '../../config';
import './AdminDashboard.css'; // Reuse common layout styles
import './AdminUsers.css'; // Reuse common table/pagination styles
import './AdminPurchases.css';

const AdminPurchases = () => {
    const { t } = useTranslation(['admin', 'common']);
    const [payments, setPayments] = useState([]);

    const [totalPayments, setTotalPayments] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const recordsPerPage = 20;

    useEffect(() => {
        fetchPayments();
    }, [currentPage, searchTerm]);

    const fetchPayments = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}get_all_payments.php?page=${currentPage}&limit=${recordsPerPage}&search=${encodeURIComponent(searchTerm)}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminId')}`
                },
                credentials: 'include'
            });
            const data = await response.json();
            if (data.status === 'success') {
                setPayments(data.payments);
                setTotalPayments(data.total_payments);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError(t('admin:purchases.errors.loadFailed', 'Failed to load purchases'));
        } finally {

            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalPayments / recordsPerPage);
    const indexOfFirstRecord = (currentPage - 1) * recordsPerPage;
    const indexOfLastRecord = Math.min(currentPage * recordsPerPage, totalPayments);

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'SUCCESS': return 'status-success';
            case 'PENDING': return 'status-pending';
            case 'FAILED': return 'status-failed';
            case 'CANCELLED': return 'status-cancelled';
            default: return '';
        }
    };

    if (loading && payments.length === 0) return (
        <div className="admin-loading-screen">
            <div className="loader-inner">
                <div className="loader-orbit"></div>
                <div className="loader-text">{t('admin:purchases.loading', 'Loading Purchase History...')}</div>
            </div>
        </div>

    );

    return (
        <div className={`admin-layout${sidebarOpen ? '' : ' sidebar-collapsed'}`}>
            <AdminSidebar sidebarOpen={sidebarOpen} onToggle={() => setSidebarOpen(o => !o)} />

            <main className="admin-main">
                <header className="main-header">
                    <button
                        className="sidebar-toggle-btn"
                        onClick={() => setSidebarOpen(o => !o)}
                        aria-label="Toggle sidebar"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '42px',
                            height: '42px',
                            fontSize: '1.4rem',
                            color: '#f8fafc',
                            background: 'rgba(59,130,246,0.15)',
                            border: '1px solid rgba(59,130,246,0.4)',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            flexShrink: 0,
                            padding: 0,
                        }}
                    >
                        ☰
                    </button>
                    <div className="header-search">
                        <input
                            type="text"
                            placeholder={t('admin:purchases.searchPlaceholder', 'Search by Order ID, Email or Name...')}
                            value={searchTerm}
                            onChange={handleSearch}
                        />

                        <span className="search-icon">🔍</span>
                    </div>
                    <div className="header-actions">
                        <div className="date-display">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                    </div>
                </header>

                <section className="content-inner">
                    <div className="page-title-section">
                        <h1>{t('admin:purchases.title', 'Purchase Management')}</h1>
                        <p>{t('admin:purchases.subtitle', 'Track all transactions and subscription payments on the platform.')}</p>
                    </div>


                    {error && <div className="error-alert">{error}</div>}

                    <div className="data-panel full-width">
                        <div className="panel-header">
                            <div className="panel-info">
                                <h2>{t('admin:purchases.allTransactions', { count: totalPayments, defaultValue: `All Transactions (${totalPayments})` })}</h2>
                                {totalPayments > 0 && (
                                    <p className="pagination-info">{t('admin:users.showing', { start: indexOfFirstRecord + 1, end: indexOfLastRecord, total: totalPayments, defaultValue: `Showing ${indexOfFirstRecord + 1}-${indexOfLastRecord} of ${totalPayments}` })}</p>
                                )}
                            </div>

                            <button className="export-btn" onClick={() => window.print()}>{t('admin:purchases.exportReport', 'Export Report')}</button>

                        </div>

                        <div className="table-wrapper">
                            <table className="premium-table">
                                <thead>
                                    <tr>
                                        <th>{t('admin:purchases.table.transactionId', 'Transaction ID')}</th>
                                        <th>{t('admin:purchases.table.userDetails', 'User Details')}</th>
                                        <th>{t('admin:purchases.table.plan', 'Plan')}</th>
                                        <th>{t('admin:purchases.table.amount', 'Amount')}</th>
                                        <th>{t('admin:purchases.table.status', 'Status')}</th>
                                        <th>{t('admin:purchases.table.date', 'Date')}</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {payments.map((payment, index) => (
                                        <tr key={payment.id || index}>
                                            <td>
                                                <code className="order-id">{payment.order_id}</code>
                                            </td>
                                            <td>
                                                <div className="user-info-text">
                                                    <p className="user-name">{payment.full_name}</p>
                                                    <p className="user-email">{payment.email}</p>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="plan-name">{payment.plan_name}</span>
                                            </td>
                                            <td>
                                                <span className="amount">₹{payment.amount}</span>
                                            </td>
                                            <td>
                                                <span className={`status-pill ${getStatusColor(payment.status)}`}>
                                                    {payment.status}
                                                </span>
                                            </td>
                                            <td>{new Date(payment.created_at).toLocaleDateString()}</td>
                                        </tr>
                                    ))}
                                    {payments.length === 0 && !loading && (
                                        <tr>
                                            <td colSpan="6" className="empty-state">{t('admin:purchases.table.empty', 'No transactions found matching your criteria')}</td>
                                        </tr>
                                    )}

                                </tbody>
                            </table>
                            {loading && payments.length > 0 && (
                                <div className="table-loading-overlay">Updating...</div>
                            )}
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="pagination-controls">
                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1 || loading}
                                    className="page-btn prev"
                                >
                                    &laquo; {t('admin:pagination.previous', 'Previous')}
                                </button>

                                <div className="page-numbers">
                                    {[...Array(totalPages)].map((_, index) => {
                                        const pageNum = index + 1;
                                        if (
                                            pageNum === 1 ||
                                            pageNum === totalPages ||
                                            (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                                        ) {
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => paginate(pageNum)}
                                                    disabled={loading}
                                                    className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        } else if (
                                            pageNum === currentPage - 3 ||
                                            pageNum === currentPage + 3
                                        ) {
                                            return <span key={pageNum} className="pagination-ellipsis">...</span>;
                                        }
                                        return null;
                                    })}
                                </div>
                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages || loading}
                                    className="page-btn next"
                                >
                                    {t('admin:pagination.next', 'Next')} &raquo;
                                </button>

                            </div>
                        )}
                    </div>
                </section>
            </main>
        </div>
    );
};

export default AdminPurchases;
